import { useState } from "react";
import {
  Badge,
  Button,
  Group,
  Loader,
  Popover,
  Radio,
  SimpleGrid,
  Stack,
  Text,
} from "@mantine/core";
import { IconCalendar } from "@tabler/icons-react";
import { BankHoliday } from "@jasper/client-models";
import { CustomDateSelection } from "@jasper/shared";
import {
  countBusinessDaysUntil,
  DELIVERY_OPTION_LABELS,
  DeliveryOption,
  formatDeliveryDate,
  getDeliveryOption,
  isExpressDate,
} from "./delivery.utils";
import { pluralize } from "./review.utils";

const DeliveryDateOption = ({
  value,
  date,
  bankHolidays,
  isExpress,
  onClick,
}: {
  value: DeliveryOption;
  date: Date;
  bankHolidays: BankHoliday[];
  isExpress: boolean;
  onClick?: () => void;
}) => {
  const businessDays = countBusinessDaysUntil(date, bankHolidays);

  return (
    <Radio.Card value={value} radius="md" p="md" onClick={onClick}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={0}>
          <Text variant="caption">{DELIVERY_OPTION_LABELS[value]}</Text>
          <Text variant="strong">{formatDeliveryDate(date)}</Text>
          <Text variant="caption">
            {pluralize(businessDays, "jour ouvré", "jours ouvrés")}
          </Text>
        </Stack>
        <Stack gap="xs" align="flex-end">
          <Radio.Indicator />
          {isExpress && (
            <Badge variant="tag" color="violet">
              Express
            </Badge>
          )}
        </Stack>
      </Group>
    </Radio.Card>
  );
};

export const DeliveryDateOptions = ({
  deliveryDate,
  onDeliveryDateChange,
  estimatedOptimalDeliveryDate,
  estimatedRushDeliveryDate,
  hasEstimationFailed,
  bankHolidays,
  onMonthChange,
  error,
}: {
  deliveryDate?: Date;
  onDeliveryDateChange: (newDeliveryDate: Date) => void;
  estimatedOptimalDeliveryDate?: Date;
  estimatedRushDeliveryDate?: Date;
  hasEstimationFailed: boolean;
  bankHolidays: BankHoliday[];
  onMonthChange: (date: Date) => void;
  error?: string;
}) => {
  const [isCalendarOpened, setIsCalendarOpened] = useState(false);

  const label = (
    <Text variant="eyebrow" c="dimmed">
      Date de livraison
    </Text>
  );

  if (hasEstimationFailed) {
    return (
      <Stack gap="xs">
        {label}
        <Text size="sm" c="error">
          Les dates de livraison n&#39;ont pas pu être calculées. Veuillez
          recharger la page.
        </Text>
      </Stack>
    );
  }

  if (
    !deliveryDate ||
    !estimatedOptimalDeliveryDate ||
    !estimatedRushDeliveryDate
  ) {
    return (
      <Stack gap="xs">
        {label}
        <Loader size="sm" />
      </Stack>
    );
  }

  // Derived from the delivery date, so a change refused in a confirmation modal keeps the previous selection
  const selectedOption = getDeliveryOption({
    deliveryDate,
    estimatedOptimalDeliveryDate,
    estimatedRushDeliveryDate,
  });

  const handleOptionChange = (option: string) => {
    if (option === "optimal") {
      onDeliveryDateChange(estimatedOptimalDeliveryDate);
    } else if (option === "express") {
      onDeliveryDateChange(estimatedRushDeliveryDate);
    }
  };

  return (
    <Stack gap="xs">
      {label}
      <Radio.Group value={selectedOption} onChange={handleOptionChange}>
        <SimpleGrid cols={3} spacing="sm">
          <DeliveryDateOption
            value="optimal"
            date={estimatedOptimalDeliveryDate}
            bankHolidays={bankHolidays}
            isExpress={false}
          />
          <DeliveryDateOption
            value="express"
            date={estimatedRushDeliveryDate}
            bankHolidays={bankHolidays}
            isExpress
          />
          {/* Only shown once a date has been picked in the calendar, clicking it reopens the calendar */}
          {selectedOption === "custom" && (
            <DeliveryDateOption
              value="custom"
              date={deliveryDate}
              bankHolidays={bankHolidays}
              isExpress={isExpressDate(
                deliveryDate,
                estimatedOptimalDeliveryDate
              )}
              onClick={() => setIsCalendarOpened(true)}
            />
          )}
        </SimpleGrid>
      </Radio.Group>
      {error && (
        <Text size="sm" c="error">
          {error}
        </Text>
      )}
      <Group>
        <Popover
          opened={isCalendarOpened}
          onChange={setIsCalendarOpened}
          position="bottom-start"
          shadow="lg"
          radius="lg"
        >
          <Popover.Target>
            <Button
              variant="default"
              size="xs"
              leftSection={<IconCalendar size={14} />}
              onClick={() => setIsCalendarOpened(opened => !opened)}
              style={{ borderStyle: "dashed" }}
            >
              Sélectionner une date
            </Button>
          </Popover.Target>
          <Popover.Dropdown>
            <CustomDateSelection
              selectDate={newDeliveryDate => {
                onDeliveryDateChange(newDeliveryDate);
                setIsCalendarOpened(false);
              }}
              optimalDeliveryDate={estimatedOptimalDeliveryDate}
              minimalRushDeliveryDate={estimatedRushDeliveryDate}
              selectedDate={deliveryDate}
              bankHolidays={bankHolidays.map(bankHoliday => ({
                beginDate: new Date(bankHoliday.startDate),
                endDate: new Date(bankHoliday.endDate),
              }))}
              onMonthChange={onMonthChange}
            />
          </Popover.Dropdown>
        </Popover>
      </Group>
    </Stack>
  );
};
